import React, { useState, useEffect } from 'react';
import { LogMessage, LogLevel } from '../../engine/core/Logger';

const LogLevelStyles: { [key: string]: React.CSSProperties } = {
    [LogLevel.Debug]: { color: '#999', backgroundColor: 'rgba(0, 0, 0, 0.6)' },
    [LogLevel.Info]: { color: '#0f0', backgroundColor: 'rgba(0, 20, 0, 0.7)' },
    [LogLevel.Warn]: { color: '#ff0', backgroundColor: 'rgba(20, 20, 0, 0.7)' },
    [LogLevel.Error]: { color: '#f00', backgroundColor: 'rgba(20, 0, 0, 0.7)', fontWeight: 'bold' },
};

const CopiedStyles: React.CSSProperties = {
    backgroundColor: '#0a0', // Flash green on copy
    transition: 'background-color 0.1s ease-in-out',
};

interface DebugConsoleProps {
    messages: LogMessage[];
}

const styles: { [key: string]: React.CSSProperties } = {
    console: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        width: '400px',
        maxHeight: '500px',
        overflowY: 'auto',
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '5px',
    },
    messageBase: {
        color: '#fff',
        fontFamily: 'monospace',
        fontSize: '12px',
        padding: '4px 8px',
        borderRadius: '4px',
        marginBottom: '5px',
    },
};

const FADE_OUT_TIME = 500; // ms
const MESSAGE_LIFETIME = 60000; // ms - Keep messages for 60 seconds

interface MessageProps {
    msg: LogMessage;
    onCopy: (msg: LogMessage) => void;
    isCopied: boolean;
}

const Message: React.FC<MessageProps> = ({ msg, onCopy, isCopied }) => {
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const fadeTimer = setTimeout(() => {
            setVisible(false);
        }, MESSAGE_LIFETIME);

        return () => {
            clearTimeout(fadeTimer);
        };
    }, []);

    return (
        <div
            onClick={() => onCopy(msg)}
            style={{
                ...styles.messageBase,
                ...LogLevelStyles[msg.level],
                ...(isCopied ? CopiedStyles : {}),
                cursor: 'pointer',
                overflow: 'hidden',
                transition: `all ${FADE_OUT_TIME}ms ease-out`,
                opacity: visible ? 1 : 0,
                maxHeight: visible ? 'none' : '0px',
                paddingTop: visible ? '4px' : 0,
                paddingBottom: visible ? '4px' : 0,
                marginBottom: visible ? '5px' : 0,
            }}
        >
            {`[${new Date(msg.timestamp).toLocaleTimeString()}] ${msg.message}`}
        </div>
    );
};

const DebugConsole: React.FC<DebugConsoleProps> = ({ messages }) => {
    const [copied, setCopied] = useState<number | null>(null);

    const handleCopy = (msg: LogMessage) => {
        const logText = `[${new Date(msg.timestamp).toLocaleTimeString()}] [${msg.level}] ${msg.message}`;
        navigator.clipboard.writeText(logText).then(() => {
            setCopied(msg.timestamp);
            setTimeout(() => setCopied(null), 200); // Reset flash after 200ms
        });
    };

    return (
        <div style={styles.console}>
            {/* Always visible header */}
            <div style={{
                ...styles.messageBase,
                backgroundColor: 'rgba(0, 50, 0, 0.9)',
                color: '#0f0',
                fontWeight: 'bold',
                marginBottom: '10px',
                textAlign: 'center'
            }}>
                🔍 Engine Log ({messages.length} messages)
            </div>
            
            {messages.map((msg, index) => (
                <Message
                    key={`${msg.timestamp}-${index}`}
                    msg={msg}
                    onCopy={handleCopy}
                    isCopied={copied === msg.timestamp}
                />
            ))}
        </div>
    );
};

export default DebugConsole; 